package ai.jessie.web.customers

import ai.jessie.shared.customers.CustomerBillingResponse
import ai.jessie.shared.customers.CustomerPreviewResponse
import ai.jessie.shared.customers.CustomerProblemResponse
import ai.jessie.shared.customers.CustomerResponse
import ai.jessie.shared.customers.SaveCustomerRequest
import ai.jessie.web.onboarding.apiRequest
import ai.jessie.web.onboarding.decodeProblem
import ai.jessie.web.onboarding.validOrganisationId
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.serialization.json.Json

// UC-012. A null customer ID addresses the collection: preview or save of a new customer.
interface CustomerPort {
    suspend fun list(organisationId: String): List<CustomerResponse>

    suspend fun read(organisationId: String, customerId: String): CustomerResponse

    suspend fun preview(
        organisationId: String,
        customerId: String?,
        request: SaveCustomerRequest,
    ): CustomerPreviewResponse

    suspend fun save(
        organisationId: String,
        customerId: String?,
        request: SaveCustomerRequest,
    ): CustomerResponse

    // A3: the separate finance projection; never derived from the management records.
    suspend fun billing(organisationId: String): List<CustomerBillingResponse>
}

val LocalCustomerPort =
    staticCompositionLocalOf<CustomerPort> {
        error("Customer port must be supplied by the application")
    }

class CustomerRequestFailure(val status: Int, val detail: CustomerProblemResponse) :
    Exception("Customer request failed")

class BrowserCustomerPort : CustomerPort {
    private val json = Json { ignoreUnknownKeys = true }

    override suspend fun list(organisationId: String) =
        json.decodeFromString<List<CustomerResponse>>(request(organisationId, "customers"))

    override suspend fun read(organisationId: String, customerId: String) =
        json.decodeFromString<CustomerResponse>(
            request(organisationId, "customers${child(customerId)}")
        )

    override suspend fun preview(
        organisationId: String,
        customerId: String?,
        request: SaveCustomerRequest,
    ) =
        json.decodeFromString<CustomerPreviewResponse>(
            request(organisationId, "customers${child(customerId)}/validate", request)
        )

    override suspend fun save(
        organisationId: String,
        customerId: String?,
        request: SaveCustomerRequest,
    ) =
        json.decodeFromString<CustomerResponse>(
            request(organisationId, "customers${child(customerId)}", request)
        )

    override suspend fun billing(organisationId: String) =
        json.decodeFromString<List<CustomerBillingResponse>>(
            request(organisationId, "customer-billing")
        )

    private fun child(customerId: String?): String {
        if (customerId == null) return ""
        require(validCustomerId(customerId))
        return "/$customerId"
    }

    private suspend fun request(
        organisationId: String,
        path: String,
        body: SaveCustomerRequest? = null,
    ): String {
        require(validOrganisationId(organisationId))
        return apiRequest(
            json,
            "/organisations/$organisationId/$path",
            body?.let { json.encodeToString(it) },
        ) { status, text ->
            val detail = json.decodeProblem(text, CustomerProblemResponse(""))
            CustomerRequestFailure(status, detail)
        }
    }
}

// Customer public IDs share the organisation ID format.
fun validCustomerId(value: String) = validOrganisationId(value)
